import { getAdminNavigationItems, getNavigationItems } from "./navigationData";
import NavigationCard from "./NavigationCard";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNativePlatform } from "@/hooks/useNativePlatform";

import { useIsNativeTablet } from "@/hooks/useIsNativeTablet";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { UserType } from "@/types/user";
import { useCurrentAdminPermissions } from "@/hooks/useCurrentAdminPermissions";
import { useMyObjectsAttention } from "@/hooks/useMyObjectsAttention";

interface NavigationCardsProps {
  pendingRequestsCount?: number;
  supportUnreadCount?: number;
}

const NavigationCards = ({
  pendingRequestsCount = 0,
  supportUnreadCount = 0,
}: NavigationCardsProps) => {
  const isMobile = useIsMobile();
  const { isNative } = useNativePlatform();
  const isNativeTablet = useIsNativeTablet();
  const useCompactGrid = isMobile || isNative;
  const { user } = useAuth();
  const { canManageSupport } = useCurrentAdminPermissions();
  const { myObjectsNeedsAction } = useMyObjectsAttention();

  if (!user) {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-muted-foreground">Laddar navigering...</p>
      </div>
    );
  }

  const navigationItems = getNavigationItems();
  const adminNavigationItems = getAdminNavigationItems({
    canManageSupport,
  });

  // Native tablet should use the desktop-like grid so cards fill rows nicely.
  if (useCompactGrid && !isNativeTablet) {
    // Previous mobile structure (restored): 
    // postAd(4)+myObjects/economy(3) | support(2)+listings(3) | settings(3)+fav/leadbox(2) | test(4)
    // myObjects uses col-span-3 in a 7-col row so it stays readable.
    return (
      <div className={cn("space-y-2")}>
        <div className="grid grid-cols-7 gap-2">
          <NavigationCard item={navigationItems[0]} />
          <NavigationCard
            item={
              user.type === UserType.Admin
                ? adminNavigationItems[1]
                : navigationItems[2]
            }
            supportUnreadCount={supportUnreadCount}
            showAttention={
              user.type !== UserType.Admin && myObjectsNeedsAction
            }
          />
        </div>

        <div className="grid grid-cols-5 gap-2">
          <NavigationCard
            item={
              user.type === UserType.Admin
                ? adminNavigationItems[0]
                : navigationItems[3]
            }
            supportUnreadCount={supportUnreadCount}
          />
          <NavigationCard item={navigationItems[1]} />
        </div>

        <div className="grid grid-cols-5 gap-2">
          <NavigationCard
            item={navigationItems[4]}
            pendingRequestsCount={pendingRequestsCount}
          />
          <NavigationCard
            item={
              user.type === UserType.Admin
                ? adminNavigationItems[3]
                : navigationItems[6]
            }
          />
        </div>

        <div className="grid grid-cols-4 gap-2">
          <NavigationCard
            item={
              user.type === UserType.Admin
                ? adminNavigationItems[2]
                : navigationItems[5]
            }
          />
        </div>
      </div>
    );
  }

  // Desktop: postAd(4)+settings(3)+listings(3)=10 | myObjects(3)+test(4)+support(2)+fav(2)=11
  return (
    <div className="space-y-2 w-[85%] mx-auto">
      <div className="grid grid-cols-10 gap-2 mb-2">
        <NavigationCard item={navigationItems[0]} />
        <NavigationCard
          item={navigationItems[4]}
          pendingRequestsCount={pendingRequestsCount}
        />
        <NavigationCard item={navigationItems[1]} />
      </div>

      <div className="grid grid-cols-11 gap-2 mb-2">
        <NavigationCard
          item={
            user.type === UserType.Admin
              ? adminNavigationItems[1]
              : navigationItems[2]
          }
          supportUnreadCount={supportUnreadCount}
          showAttention={
            user.type !== UserType.Admin && myObjectsNeedsAction
          }
        />
        <NavigationCard
          item={
            user.type === UserType.Admin
              ? adminNavigationItems[2]
              : navigationItems[5]
          }
        />
        <NavigationCard
          item={
            user.type === UserType.Admin
              ? adminNavigationItems[0]
              : navigationItems[3]
          }
          supportUnreadCount={supportUnreadCount}
        />
        <NavigationCard
          item={
            user.type === UserType.Admin
              ? adminNavigationItems[3]
              : navigationItems[6]
          }
        />
      </div>
    </div>
  );
};

export default NavigationCards;
